import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { JobConfig } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { stepModelsSummary } from '../../job/StepModelsField';
import { useDeleteJobConfigMutation, useListJobConfigsQuery } from '../jobConfigEndpoints';

/**
 * QZ-D194, EXJ-D3: the admin-made presets a GPS Quiz user's job may run on. A table of every
 * configuration with a delete button per row, as `SourcesPage.tsx`'s list.
 */
export const JobConfigsPage = () => {
  const { t } = useTranslation();
  const { data, error: listError, isLoading } = useListJobConfigsQuery();
  const [deleteJobConfig, { isLoading: deleting }] = useDeleteJobConfigMutation();

  const [confirmingId, setConfirmingId] = useState<string | undefined>();
  const [deleteError, setDeleteError] = useState<string | undefined>();

  const listCode = problemCode(listError);
  const jobConfigs = data?.jobConfigs ?? [];

  const openDeleteConfirm = (jobConfigId: string) => {
    setDeleteError(undefined);
    setConfirmingId(jobConfigId);
  };

  const handleDelete = async (jobConfigId: string) => {
    setDeleteError(undefined);
    try {
      await deleteJobConfig(jobConfigId).unwrap();
      setConfirmingId(undefined);
    } catch (thrown) {
      const code = problemCode(thrown);
      setDeleteError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  const targetLanguagesLabel = (jobConfig: JobConfig): string =>
    t(`jobConfig.targetLanguages.${jobConfig.targetLanguages}`);

  return (
    <main className="qz-main">
      <div className="qz-container">
        <div className="qz-page-header">
          <div>
            <h1>{t('jobConfig.list.title')}</h1>
            <p className="qz-sub">{t('jobConfig.list.sub')}</p>
          </div>
          <Link className="btn btn-primary" to="/job-configs/new">
            <Icon name="plus" /> {t('jobConfig.list.new')}
          </Link>
        </div>

        {listError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{t(listCode ? `error.${listCode}` : 'error.unknown')}</div>
          </div>
        )}
        {deleteError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{deleteError}</div>
          </div>
        )}

        <div className="qz-card">
          {!isLoading && !listError && jobConfigs.length === 0 && (
            <div className="qz-empty">{t('jobConfig.list.empty')}</div>
          )}
          {!isLoading && !listError && jobConfigs.length > 0 && (
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>{t('jobConfig.list.col.id')}</th>
                  <th>{t('jobConfig.list.col.description')}</th>
                  <th>{t('jobConfig.list.col.models')}</th>
                  <th>{t('jobConfig.list.col.approval')}</th>
                  <th>{t('jobConfig.list.col.targetLanguages')}</th>
                  <th>{t('jobConfig.list.col.maxTaskCount')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobConfigs.map((jobConfig) => (
                  <tr key={jobConfig.jobConfigId}>
                    <td className="fw-semibold">
                      <Link to={`/job-configs/${encodeURIComponent(jobConfig.jobConfigId)}`}>
                        {jobConfig.jobConfigId}
                      </Link>
                    </td>
                    <td className="qz-small">{jobConfig.description}</td>
                    <td className="qz-small qz-muted">
                      {stepModelsSummary(t, jobConfig.stepModels)}
                    </td>
                    <td className="qz-small">{t(`job.approval.${jobConfig.approval}`)}</td>
                    <td className="qz-small">{targetLanguagesLabel(jobConfig)}</td>
                    <td className="qz-small">{jobConfig.maxTaskCount}</td>
                    <td className="text-end">
                      {confirmingId === jobConfig.jobConfigId ? (
                        <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                          <span className="qz-small">{t('jobConfig.list.deleteConfirm')}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={deleting}
                            onClick={() => handleDelete(jobConfig.jobConfigId)}
                          >
                            {t('jobConfig.list.delete')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={deleting}
                            onClick={() => setConfirmingId(undefined)}
                          >
                            {t('job.new.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openDeleteConfirm(jobConfig.jobConfigId)}
                        >
                          {t('jobConfig.list.delete')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

export default JobConfigsPage;
